"use client";
import Image from "next/image";
import { useState } from "react";

interface LazyImageProps {
    src: string;
    alt: string;
    className?: string;
    wrapperClassName?: string;
    loading?: "lazy" | "eager";
    width?: number;
    height?: number;
    sizes?: string;
    priority?: boolean;
    fetchPriority?: "high" | "low" | "auto";
}

/**
 * LazyImage — drop-in replacement for <img>.
 * Now powered by next/image for automatic optimization,
 * while keeping the animated shimmer transition.
 */
const LazyImage = ({
    src,
    alt,
    className = "",
    wrapperClassName = "",
    loading = "lazy",
    width,
    height,
    sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    priority = false,
    fetchPriority = "auto",
}: LazyImageProps) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    const isFixedSize = width !== undefined && height !== undefined;

    return (
        <div className={`relative overflow-hidden ${wrapperClassName}`}>
            {/* Shimmer — visible until image loads */}
            {!loaded && !error && (
                <div className="absolute inset-0 shimmer-bg" aria-hidden="true" />
            )}

            {!error ? (
                <Image
                    src={src}
                    alt={alt}
                    width={isFixedSize ? width : undefined}
                    height={isFixedSize ? height : undefined}
                    fill={!isFixedSize}
                    priority={priority}
                    fetchPriority={fetchPriority}
                    sizes={sizes}
                    className={`${className} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"
                        }`}
                    onLoad={() => setLoaded(true)}
                    onError={() => {
                        setError(true);
                        setLoaded(true);
                    }}
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground text-xs">
                    🖼️
                </div>
            )}
        </div>
    );
};

export default LazyImage;
